import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Package, Plus, Search, Filter, Download, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, Clock, AlertTriangle,
  Database, RotateCcw, Minus, Save, Loader2
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inventoryApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  draft: { label: 'پیش‌نویس', variant: 'neutral' as const, icon: Clock },
  pending: { label: 'در انتظار تایید', variant: 'warning' as const, icon: Clock },
  completed: { label: 'تکمیل شده', variant: 'success' as const, icon: CheckCircle },
  cancelled: { label: 'لغو شده', variant: 'danger' as const, icon: XCircle },
}

export default function StockAdjustmentsPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<AdjustmentListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.adjustments(filters),
    queryFn: () => inventoryApi.listAdjustments(filters),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => inventoryApi.createAdjustment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adjustments() })
      toast.success('تنظیم موجودی ایجاد شد')
      closeModal('create-adjustment')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ایجاد ناموفق بود'),
  })

  const completeMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.completeAdjustment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adjustments() })
      toast.success('تنظیم موجودی تکمیل شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'تکمیل ناموفق بود'),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.cancelAdjustment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adjustments() })
      toast.success('تنظیم موجودی لغو شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'لغو ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.deleteAdjustment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adjustments() })
      toast.success('تنظیم موجودی حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getRowDropdownItems = (adjustment: any) => [
    { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/inventory/adjustments/${adjustment.id}`, '_blank') },
    { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('stock', 'adjust') || adjustment.status !== 'draft' },
    { type: 'divider' },
    { label: 'تکمیل', icon: CheckCircle, onClick: () => completeMutation.mutate(adjustment.id), disabled: !hasPermission('stock', 'adjust') || adjustment.status !== 'pending' },
    { label: 'لغو', icon: XCircle, className: 'text-warning-600', onClick: () => cancelMutation.mutate(adjustment.id), disabled: !hasPermission('stock', 'adjust') || adjustment.status === 'completed' || adjustment.status === 'cancelled' },
    { type: 'divider' },
    { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(adjustment.id); openModal('delete-confirm') }, disabled: !hasPermission('stock', 'adjust') || adjustment.status !== 'draft' },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">تنظیم موجودی</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} تنظیم ثبت شده
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-adjustment')} disabled={!hasPermission('stock', 'adjust')}>
            <Plus className="w-4 h-4" />
            تنظیم جدید
          </Button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (شماره تنظیم، انبار، دلیل)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'draft', label: 'پیش‌نویس' },
              { value: 'pending', label: 'در انتظار تایید' },
              { value: 'completed', label: 'تکمیل شده' },
              { value: 'cancelled', label: 'لغو شده' },
            ]}
            placeholder="وضعیت"
            className="w-40"
          />
          <Select
            value={filters.warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارها' }]}
            placeholder="انبار"
            className="w-40"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره تنظیم</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">انبار</th>
                    <th>آیتم‌ها</th>
                    <th>مجموع اختلاف</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton variant="text" width="120px" /></td>
                      <td className="hidden sm:table-cell"><Skeleton variant="text" width="80px" /></td>
                      <td className="hidden md:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="60px" /></td>
                      <td><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="rectangular" width="100px" height="24px" /></td>
                      <td><Skeleton variant="rectangular" width="32px" height="32px" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری تنظیمات</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <Database className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ تنظیم موجودی یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین تنظیم موجودی را ایجاد کنید</p>
            <Button onClick={() => openModal('create-adjustment')} disabled={!hasPermission('stock', 'adjust')}>
              <Plus className="w-4 h-4 ml-1" />
              ایجاد تنظیم
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره تنظیم</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">انبار</th>
                    <th>آیتم‌ها</th>
                    <th>مجموع اختلاف</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((adjustment, index) => {
                    const statusInfo = statusConfig[adjustment.status as keyof typeof statusConfig] || statusConfig.draft
                    return (
                      <motion.tr
                        key={adjustment.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <td className="font-medium text-neutral-900 dark:text-white font-mono">{adjustment.adjustment_number}</td>
                        <td className="hidden sm:table-cell text-neutral-600 dark:text-neutral-400 text-sm">{formatDate(adjustment.created_at)}</td>
                        <td className="hidden md:table-cell text-neutral-700 dark:text-neutral-300">{adjustment.warehouse?.name || '—'}</td>
                        <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{adjustment.lines?.length || 0}</td>
                        <td className="font-mono text-sm {adjustment.total_difference > 0 ? 'text-success-600' : adjustment.total_difference < 0 ? 'text-danger-600' : 'text-neutral-900'} dark:text-neutral-300">
                          {adjustment.total_difference > 0 ? '+' : ''}{formatNumber(adjustment.total_difference)}
                        </td>
                        <td>
                          <Badge variant={statusInfo.variant} className="text-xs flex items-center gap-1">
                            <statusInfo.icon className="w-3 h-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={getRowDropdownItems(adjustment)}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Create Adjustment Modal */}
      <Modal
        isOpen={modals['create-adjustment']}
        onClose={() => closeModal('create-adjustment')}
        title="ایجاد تنظیم موجودی جدید"
        size="lg"
      >
        <AdjustmentForm onSubmit={createMutation.mutate} onClose={() => closeModal('create-adjustment')} isLoading={createMutation.isPending} />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف تنظیم موجودی"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این تنظیم موجودی مطمئن هستید؟
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const adjustmentItemSchema = z.object({
  product_id: z.string().uuid('محصول معتبر نیست'),
  variant_id: z.string().uuid().optional().nullable(),
  counted_quantity: z.number().min(0, 'تعداد شمرده شده باید مثبت باشد'),
  reason: z.string().optional(),
})

const adjustmentFormSchema = z.object({
  warehouse_id: z.string().uuid('انبار معتبر نیست'),
  reason: z.string().optional(),
  notes: z.string().optional(),
  items: z.array(adjustmentItemSchema).min(1, 'حداقل یک آیتم باید وجود داشته باشد'),
})

type AdjustmentFormData = z.infer<typeof adjustmentFormSchema>

interface AdjustmentFormProps {
  onSubmit: (data: AdjustmentFormData) => void
  onClose: () => void
  isLoading: boolean
}

function AdjustmentForm({ onSubmit, onClose, isLoading }: AdjustmentFormProps) {
  const methods = useForm<AdjustmentFormData>({
    resolver: zodResolver(adjustmentFormSchema),
    defaultValues: {
      warehouse_id: '',
      reason: '',
      notes: '',
      items: [{ product_id: '', variant_id: null, counted_quantity: 0, reason: '' }],
    },
  })

  const { control, watch } = methods
  const { fields: itemFields, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: 'items',
  })

  const totalDifference = watch('items')?.reduce((sum: number, item: any) => {
    // system_quantity would come from API, for now just show counted
    return sum + (item.counted_quantity || 0)
  }, 0) || 0

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="warehouse_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="انبار *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="انبار" {...field} />
          )}
        />
        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <Select
              label="دلیل تنظیم"
              options={[
                { value: '', label: 'انتخاب کنید' },
                { value: 'count', label: 'شمارش دوره‌ای' },
                { value: 'damage', label: 'خرابی/تخریب' },
                { value: 'loss', label: 'کمبود/از دست رفتن' },
                { value: 'found', label: 'یافتن کالا' },
                { value: 'correction', label: 'تصحیح خطا' },
                { value: 'expiry', label: 'انقضای تاریخ' },
                { value: 'other', label: 'سایر' },
              ]}
              placeholder="دلیل تنظیم"
              {...field}
            />
          )}
        />
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <Input label="یادداشت‌ها" placeholder="اختیاری" {...field} className="md:col-span-2" />
          )}
        />
      </div>

      <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-neutral-900 dark:text-white">آیتم‌های تنظیم</h4>
          <Button type="button" variant="outline" size="sm" onClick={() => appendItem({ product_id: '', variant_id: null, counted_quantity: 0, reason: '' })}>
            <Plus className="w-4 h-4 ml-1" />
            افزودن آیتم
          </Button>
        </div>

        {itemFields.map((field, index) => (
          <div key={field.id} className="card p-4 mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-white">آیتم #{index + 1}</span>
              {itemFields.length > 1 && (
                <button type="button" onClick={() => removeItem(index)} className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 dark:hover:text-danger-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Controller
                name={`items.${index}.product_id`}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select label="محصول *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="محصول" {...field} />
                )}
              />
              <Controller
                name={`items.${index}.variant_id`}
                control={control}
                render={({ field }) => (
                  <Select label="تنوع" options={[{ value: '', label: 'پیش‌فرض' }]} placeholder="تنوع" {...field} />
                )}
              />
              <Controller
                name={`items.${index}.counted_quantity`}
                control={control}
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <Input label="تعداد شمرده شده *" type="number" min={0} step={1} {...field} />
                )}
              />
              <Controller
                name={`items.${index}.reason`}
                control={control}
                render={({ field }) => (
                  <Input label="دلیل" placeholder="اختیاری" {...field} />
                )}
              />
            </div>
          </div>
        ))}

        {itemFields.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-xl">
            <Package className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
            <p className="text-neutral-500 dark:text-neutral-400">هیچ آیتمی اضافه نشده است</p>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => appendItem({ product_id: '', variant_id: null, counted_quantity: 0, reason: '' })}>
              <Plus className="w-4 h-4 ml-1" />
              افزودن آیتم اول
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            مجموع تعداد شمرده شده:
          </span>
          <span className="text-xl font-bold text-primary-600 dark:text-primary-400 font-mono">
            {formatNumber(totalDifference)}
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <RotateCcw className="w-4 h-4 ml-1" /> ثبت تنظیم </> }
        </Button>
      </div>
    </form>
  )
}

type AdjustmentListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  warehouse_id?: string
  status?: string
  date_from?: string
  date_to?: string
}